import React, { useMemo } from 'react';
import { CheckmarkIcon } from '../components/icons';

interface PasswordStrengthStatusProps {
  password: string;
}

interface Requirement {
  check: boolean;
  text: string;
}

const COLORS = {
  text: {
    passed: '#111111',
    failed: '#979797',
  },
  icon: {
    passed: '#0EDD4D',
    failed: '#D51236',
    empty: '#979797',
  },
} as const;

const MIN_PASSWORD_LENGTH = 8;
const SPECIAL_CHARS_REGEX = /[!@#$%^&*]/;

function PasswordStrengthStatus({ password }: PasswordStrengthStatusProps) {
  const isEmpty = password.length === 0;

  const requirements = useMemo<Requirement[]>(
    () => [
      {
        check: password.length >= MIN_PASSWORD_LENGTH,
        text: `${MIN_PASSWORD_LENGTH} characters minimum`,
      },
      {
        check: /[A-Z]/.test(password),
        text: 'One capital letter',
      },
      {
        check: /[a-z]/.test(password),
        text: 'One lowercase letter',
      },
      {
        check: SPECIAL_CHARS_REGEX.test(password),
        text: 'One special character',
      },
      {
        check: /[0-9]/.test(password),
        text: 'One digit',
      },
    ],
    [password]
  );

  const getTextColor = (checkPassed: boolean): string =>
    checkPassed ? COLORS.text.passed : COLORS.text.failed;

  const getIconColor = (checkPassed: boolean): string => {
    if (checkPassed) return COLORS.icon.passed;
    if (isEmpty) return COLORS.icon.empty;
    return COLORS.icon.failed;
  };

  return (
    <ul className="form-group security-password">
      {requirements.map(({ check, text }) => (
        <li
          key={text}
          className="security-password-text"
          style={{ color: getTextColor(check) }}
        >
          <CheckmarkIcon
            className="checkmark-icon"
            style={{ color: getIconColor(check) }}
            aria-hidden="true"
          />
          {text}
        </li>
      ))}
    </ul>
  );
}

export default PasswordStrengthStatus;
